import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import { useUserStore } from '@/stores/user'
import {
  fetchMySidebarTasks,
  createTask as apiCreateTask,
  changeTaskStatus,
  appendTaskDates,
  clearTaskDates,
  createSupplement,
  fetchTask,
  softDeleteTask as apiSoftDeleteTask,
  reassignTask as apiReassignTask,
} from '@/api/task'

export const PRIORITY_LABEL = { high: '高', medium: '中', low: '低', HIGH: '高', MEDIUM: '中', LOW: '低' }
export const STATUS_LABEL = {
  pending: '待办', in_progress: '进行中', paused: '已暂停', completed: '已完成',
  PENDING: '待办', IN_PROGRESS: '进行中', PAUSED: '已暂停', COMPLETED: '已完成',
}

function normalizeTask(t) {
  if (!t) return t
  return {
    id: t.id,
    taskNo: t.taskNo,
    title: t.title,
    projectId: t.projectId,
    projectName: t.projectName || '',
    creator: t.creatorName || t.creator || '',
    creatorUserId: t.creatorUserId,
    executor: t.executorName || t.executor || '',
    executorUserId: t.executorUserId,
    createdAt: t.createdAt,
    desc: t.description ?? t.desc ?? '',
    priority: (t.priority || '').toLowerCase(),
    dates: (t.scheduledDates || t.dates || []).map((d) =>
      typeof d === 'string' && d.length > 10 ? d.substring(0, 10) : d
    ),
    plannedStartDate: t.plannedStartDate || '',
    plannedEndDate: t.plannedEndDate || '',
    status: (t.status || '').toLowerCase(),
    taskType: (t.taskType || 'new').toLowerCase(),
    supplements: (t.supplements || []).map((s) => ({
      id: s.id,
      content: s.content,
      author: s.authorName || s.author || '',
      createdAt: s.createdAt,
    })),
    logs: (t.logs || []).map((l) => ({
      id: l.id,
      text: l.action,
      createdAt: l.createdAt,
    })),
  }
}

export const useTaskStore = defineStore('task', () => {
  const tasks = ref([])
  const selectedIds = ref(new Set())
  const detailTaskId = ref(null)
  const calendarYear = ref(new Date().getFullYear())
  const calendarMonth = ref(new Date().getMonth())
  const loading = ref(false)

  async function init() {
    loading.value = true
    try {
      const data = await fetchMySidebarTasks()
      tasks.value = (data || []).map(normalizeTask)
    } catch {
      // silent
    } finally {
      loading.value = false
    }
  }

  const activeTasks = computed(() =>
    tasks.value.filter(
      (t) => t.status === 'pending' || t.status === 'in_progress' || t.status === 'paused'
    )
  )

  const myActiveTasks = computed(() => {
    const userStore = useUserStore()
    return activeTasks.value.filter((t) => t.executor === userStore.displayName)
  })

  function hasSchedule(t) {
    return t.dates?.length > 0
  }

  function isOnDate(t, dateStr) {
    return t.dates?.includes(dateStr)
  }

  function isScheduledToday(t) {
    return isOnDate(t, dayjs().format('YYYY-MM-DD'))
  }

  const sidebarTasks = computed(() => {
    const today = dayjs().format('YYYY-MM-DD')
    return [...myActiveTasks.value].sort((a, b) => {
      const aToday = isOnDate(a, today) ? 0 : 1
      const bToday = isOnDate(b, today) ? 0 : 1
      if (aToday !== bToday) return aToday - bToday
      const po = { high: 0, medium: 1, low: 2 }
      return (po[a.priority] ?? 1) - (po[b.priority] ?? 1)
    })
  })

  function addToDate(task, dateStr) {
    if (!task.dates) task.dates = []
    if (!task.dates.includes(dateStr)) {
      task.dates.push(dateStr)
      task.dates.sort()
    }
  }

  const scheduledTasks = computed(() => {
    const list = myActiveTasks.value.filter(hasSchedule)
    return [...list].sort((a, b) => {
      const da = [...a.dates].sort()[0]
      const db = [...b.dates].sort()[0]
      if (da !== db) return da.localeCompare(db)
      const po = { high: 0, medium: 1, low: 2 }
      return (po[a.priority] ?? 1) - (po[b.priority] ?? 1)
    })
  })

  const unscheduledTasks = computed(() => {
    const list = myActiveTasks.value.filter((t) => !hasSchedule(t))
    return [...list].sort((a, b) => {
      const po = { high: 0, medium: 1, low: 2 }
      return (po[a.priority] ?? 1) - (po[b.priority] ?? 1)
    })
  })

  function toggleSelect(id, checked) {
    const next = new Set(selectedIds.value)
    if (checked) next.add(id)
    else next.delete(id)
    selectedIds.value = next
  }

  function clearSelection() {
    selectedIds.value = new Set()
  }

  async function createTask(payload) {
    const userStore = useUserStore()
    const body = {
      title: payload.title,
      projectId: payload.projectId || null,
      description: payload.desc || '',
      priority: (payload.priority || 'medium').toUpperCase(),
      taskType: (payload.taskType || 'new').toUpperCase(),
      executorUserId: payload.executorUserId || userStore.userId,
      scheduledDates: payload.initialDate ? [payload.initialDate] : [],
    }
    const data = await apiCreateTask(body)
    const task = normalizeTask(data)
    tasks.value.push(task)
    return task
  }

  async function addSupplement(taskId, content, author) {
    const data = await createSupplement(taskId, { content })
    const entry = {
      id: data.id,
      content: data.content,
      author: data.authorName || author,
      createdAt: data.createdAt,
    }
    const task = tasks.value.find((t) => t.id === taskId)
    if (task) {
      if (!task.supplements) task.supplements = []
      task.supplements.push(entry)
      if (!task.logs) task.logs = []
      task.logs.unshift({
        id: data.id,
        text: `${entry.author} 追加补充`,
        createdAt: entry.createdAt,
      })
    }
    return entry
  }

  async function assignToDate(taskId, dateStr) {
    await appendTaskDates(taskId, [dateStr])
    const task = tasks.value.find((t) => t.id === taskId)
    if (task) addToDate(task, dateStr)
    return task
  }

  async function batchAssignToDate(taskIds, dateStr) {
    await Promise.all(taskIds.map((id) => appendTaskDates(id, [dateStr])))
    taskIds.forEach((id) => {
      const task = tasks.value.find((t) => t.id === id)
      if (task) addToDate(task, dateStr)
    })
    clearSelection()
  }

  async function completeTask(id) {
    await changeTaskStatus(id, 'COMPLETED')
    const task = tasks.value.find((t) => t.id === id)
    if (task) task.status = 'completed'
    const next = new Set(selectedIds.value)
    next.delete(id)
    selectedIds.value = next
    detailTaskId.value = null
  }

  async function setTaskStatus(id, status) {
    await changeTaskStatus(id, status.toUpperCase())
    const task = tasks.value.find((t) => t.id === id)
    if (task) task.status = status.toLowerCase()
  }

  async function clearSchedule(id) {
    await clearTaskDates(id)
    const task = tasks.value.find((t) => t.id === id)
    if (task) task.dates = []
  }

  async function deleteTask(id) {
    await apiSoftDeleteTask(id)
    tasks.value = tasks.value.filter((t) => t.id !== id)
    const next = new Set(selectedIds.value)
    next.delete(id)
    selectedIds.value = next
    if (detailTaskId.value === id) detailTaskId.value = null
  }

  async function reassignTask(id, executorUserId) {
    const data = await apiReassignTask(id, executorUserId)
    const normalized = normalizeTask(data)
    const idx = tasks.value.findIndex((t) => t.id === id)
    if (idx >= 0) {
      tasks.value[idx] = normalized
    } else {
      tasks.value.push(normalized)
    }
    return normalized
  }

  function getTaskById(id) {
    return tasks.value.find((t) => t.id === id)
  }

  async function loadTask(id) {
    const data = await fetchTask(id)
    const normalized = normalizeTask(data)
    const idx = tasks.value.findIndex((t) => t.id === id)
    if (idx >= 0) {
      tasks.value[idx] = normalized
    } else {
      tasks.value.push(normalized)
    }
    return normalized
  }

  function tasksOnDate(dateStr) {
    return myActiveTasks.value.filter((t) => isOnDate(t, dateStr))
  }

  function goToMonth(year, month) {
    calendarYear.value = year
    calendarMonth.value = month
  }

  function changeMonth(delta) {
    let y = calendarYear.value
    let m = calendarMonth.value + delta
    if (m > 11) { m = 0; y++ }
    if (m < 0) { m = 11; y-- }
    calendarYear.value = y
    calendarMonth.value = m
  }

  function goToday() {
    const now = new Date()
    calendarYear.value = now.getFullYear()
    calendarMonth.value = now.getMonth()
  }

  return {
    tasks,
    selectedIds,
    detailTaskId,
    calendarYear,
    calendarMonth,
    loading,
    activeTasks,
    myActiveTasks,
    scheduledTasks,
    unscheduledTasks,
    hasSchedule,
    isOnDate,
    isScheduledToday,
    sidebarTasks,
    toggleSelect,
    clearSelection,
    createTask,
    assignToDate,
    batchAssignToDate,
    completeTask,
    setTaskStatus,
    clearSchedule,
    deleteTask,
    reassignTask,
    getTaskById,
    loadTask,
    tasksOnDate,
    goToMonth,
    changeMonth,
    goToday,
    addSupplement,
    init,
  }
})
